import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Skeleton,
  Snackbar,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PeopleIcon from "@mui/icons-material/People";
import PaymentsIcon from "@mui/icons-material/Payments";
import LinkIcon from "@mui/icons-material/Link";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import api from "../../utils/api";

export default function AgentDashboard() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [agent, setAgent] = useState(null);

  // 🔔 copy toast
  const [toast, setToast] = useState({
    open: false,
    message: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [summaryRes, agentRes] = await Promise.all([
          api.get("/agent/summary"),
          api.get("/agent/me"),
        ]);

        setSummary(summaryRes.data);
        setAgent(agentRes.data);
      } catch (err) {
        console.error("Agent dashboard load error", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // 🔗 build referral link safely
  const referralLink = agent?.referral_code
    ? `${window.location.origin}/register?ref=${agent.referral_code}`
    : "";

  const copyText = (text, label = "Copied") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setToast({ open: true, message: `${label} copied successfully` });
  };

  const closeToast = () => {
    setToast({ open: false, message: "" });
  };

  if (loading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((i) => (
          <Grid key={i} item xs={12} md={3}>
            <Skeleton height={120} />
          </Grid>
        ))}
      </Grid>
    );
  }

  const totalCommission =
    summary?.total_commission ?? summary?.total_revenue ?? 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Agent Overview
      </Typography>

      {/* ================= KPI CARDS ================= */}
      <Grid container spacing={3}>
        <KpiCard
          title="Referred Students"
          value={summary?.total_students || 0}
          icon={<PeopleIcon />}
        />

        <KpiCard
          title="Active Subscriptions"
          value={summary?.active_subscriptions || 0}
          icon={<PaymentsIcon />}
        />

        <KpiCard
          title="Total Commission Earned"
          value={`₦${Number(totalCommission).toLocaleString()}`}
          icon={<PaymentsIcon />}
        />

        <KpiCard
          title="Agent Code"
          value={agent?.referral_code || "-"}
          icon={<LinkIcon />}
        />
      </Grid>

      {/* ================= REFERRAL LINK + QR ================= */}
      <Paper
        sx={{
          mt: 4,
          p: 3,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: 3,
          alignItems: "center",
        }}
      >
        {/* LINK INFO */}
        <Box>
          <Typography fontWeight={700}>Your Referral Link</Typography>
          <Typography variant="body2" color="text.secondary">
            Share this link or QR code to invite students and earn commissions.
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontFamily: "monospace",
              background: "#f8fafc",
              p: 1,
              borderRadius: 1,
              wordBreak: "break-all",
            }}
          >
            {referralLink || "-"}
          </Typography>

          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={() => copyText(referralLink, "Referral link")}
              disabled={!referralLink}
            >
              Copy Link
            </Button>

            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() =>
                copyText(agent?.referral_code, "Agent code")
              }
            >
              Copy Code
            </Button>
          </Box>
        </Box>

        {/* QR CODE WITH BRAND LOGO */}
        <Box
          sx={{
            textAlign: "center",
            borderLeft: { md: "1px solid #e5e7eb" },
            pl: { md: 3 },
          }}
        >
          <Typography fontWeight={700} gutterBottom>
            Referral QR Code
          </Typography>

          {referralLink ? (
            <>
              <QRCodeCanvas
                id="agent-referral-qr"
                value={referralLink}
                size={180}
                level="H"
                bgColor="#ffffff"
                fgColor="#000000"
                imageSettings={{
                  src: "/MyLogo.png",   // 👈 BRAND LOGO
                  height: 42,
                  width: 42,
                  excavate: true,     // clears background behind logo
                }}
              />

              <Button
                sx={{ mt: 2 }}
                size="small"
                startIcon={<QrCodeIcon />}
                onClick={() => {
                  const canvas =
                    document.getElementById("agent-referral-qr");
                  const pngUrl = canvas
                    .toDataURL("image/png")
                    .replace("image/png", "image/octet-stream");

                  const downloadLink = document.createElement("a");
                  downloadLink.href = pngUrl;
                  downloadLink.download = "cbt-master-referral-qr.png";
                  document.body.appendChild(downloadLink);
                  downloadLink.click();
                  document.body.removeChild(downloadLink);
                }}
              >
                Download QR
              </Button>
            </>
          ) : (
            <Typography color="text.secondary">No referral link</Typography>
          )}
        </Box>
      </Paper>

      {/* ================= QUICK ACTIONS ================= */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <QuickAction
          label="View Referred Students"
          onClick={() => navigate("/agent/students")}
        />

        <QuickAction
          label="Agent Finance"
          onClick={() => navigate("/agent/finance")}
        />
      </Grid>

      {/* ================= COPY SUCCESS TOAST ================= */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={closeToast} severity="success" variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ============================
   COMPONENTS
============================ */

function KpiCard({ title, value, icon }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Paper sx={{ p: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "rgba(25,118,210,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography fontWeight={800}>{value}</Typography>
        </Box>
      </Paper>
    </Grid>
  );
}

function QuickAction({ label, onClick }) {
  return (
    <Grid item xs={12} md={6}>
      <Paper
        onClick={onClick}
        sx={{
          p: 3,
          cursor: "pointer",
          "&:hover": { background: "#f8fafc" },
        }}
      >
        <Typography fontWeight={700}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          Go to {label.toLowerCase()}
        </Typography>
      </Paper>
    </Grid>
  );
}
